'use client'

import { useRef } from 'react'
import Image from 'next/image'
import { useParams } from 'next/navigation'

import FavoriteButton from '@components/FavoriteButton'
import { slugify } from '@lib/hooks/use-slugify'
import LocalizedClientLink from '@modules/common/components/localized-client-link'
import { ArrowRightIcon } from '@modules/common/icons'
import { useTranslations } from 'next-intl'

import UndifinedImage from '../../public/undefined-destination.webp'
import ArrowCircleButton from './ArrowInCircleButton'

export default function ChooseExperienceBanner({
  data,
  translationKey,
  user_token,
  showBtn = true,
}) {
  const { countryCode } = useParams()

  const t = useTranslations(translationKey)
  const containerRef = useRef<HTMLDivElement | null>(null)

  const scroll = (offset: number) => {
    const el = containerRef.current
    if (!el) return
    el.scrollBy({ left: offset, behavior: 'smooth' })
  }

  const slugWithId = data.products.map(
    (experience) => `${slugify(experience.title)}-${experience.id}`
  )

  return (
    <section className="flex w-full flex-col items-start justify-start gap-8">
      <div className="flex w-full flex-row flex-wrap justify-between gap-8">
        <h2
          className={
            'flex flex-col font-bebasNeue-regular text-[44px] uppercase leading-[38px] text-black large:max-w-fit large:text-[52px] large:text-[64px] large:leading-[56px]'
          }
        >
          <span>{t('chooseExperienceTitle')}</span>
          <span
            className={
              'font-playfairDisplay-bold-italic text-[26px] lowercase large:text-5xl'
            }
          >
            {t('chooseExperienceSubtitle')}
          </span>
        </h2>

        <div className="z-[50] flex hidden flex-row large:flex">
          <button onClick={() => scroll(-700)} aria-label="Scroll left">
            <ArrowCircleButton rotate color={'black'} />
          </button>

          <button onClick={() => scroll(700)} aria-label="Scroll right">
            <ArrowCircleButton color={'black'} />
          </button>
        </div>

        <div ref={containerRef} className="h-full w-full overflow-auto">
          <ul className="flex w-fit justify-start gap-4">
            {data.products.map((exp, index) => (
              <li
                key={exp.id}
                className="relative h-[370px] w-[400px] overflow-hidden rounded"
              >
                <LocalizedClientLink
                  href={`/experience/${slugWithId[index]}`}
                  className="isolate block flex h-full w-full flex-col justify-between p-8"
                >
                  <div className="absolute inset-0 -z-10">
                    <Image
                      src={
                        (exp.metadata.thumbnail_url as string) ?? UndifinedImage
                      }
                      alt={exp.title || 'Experience image'}
                      className="h-full w-full object-cover"
                      width={400}
                      height={400}
                    />
                    <div className="bg-gradient-overlay-2 absolute inset-0 z-[49]"></div>
                  </div>

                  <h2
                    className={
                      'flex flex-col font-playfairDisplay-bold-italic text-xl font-normal text-white large:text-2xl'
                    }
                  >
                    <span>{exp.title}</span>
                    <span className={'font-bebasNeue-regular text-xl'}>
                      {exp.metadata.place_name as string}
                    </span>
                  </h2>

                  <div className="flex flex-row items-end justify-between">
                    <div className="flex max-w-[80%] flex-col gap-2">
                      {(() => {
                        const timeLength = (
                          countryCode !== 'fr' &&
                          exp.metadata?.locale?.[countryCode]?.time_length
                            ? exp.metadata.locale[countryCode].time_length
                            : exp.metadata?.time_length
                        ) as string | undefined

                        if (!timeLength || timeLength.trim() === '-') return null

                        return (
                          <div className="w-fit rounded-2xl bg-green px-4 py-1 text-sm text-white">
                            <p>{timeLength}</p>
                          </div>
                        )
                      })()}

                      {(() => {
                        const priceText = (
                          countryCode !== 'fr' &&
                          exp.metadata?.locale?.[countryCode]?.price_text
                            ? exp.metadata.locale[countryCode].price_text
                            : exp.metadata?.price_text
                        ) as string | undefined

                        if (!priceText || priceText.trim() === '-') return null

                        return (
                          <div className="w-fit rounded-2xl bg-green px-4 py-1 text-sm text-white">
                            <p>{priceText}</p>
                          </div>
                        )
                      })()}
                    </div>
                  </div>
                </LocalizedClientLink>

                <div className="absolute bottom-4 right-4">
                  <FavoriteButton
                    authenticated={!!user_token}
                    clickable={true}
                    documentId={exp.metadata.strapi_document_id}
                    medusaId={exp.id}
                    documentType="experience"
                  />
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {showBtn && (
        <LocalizedClientLink
          href={'/search'}
          className="flex w-fit flex-row items-center gap-4 self-end border-b-2 border-black py-2 font-bold text-black"
        >
          {t('chooseExperienceAllExperiences')}
          <ArrowRightIcon color="#000" />
        </LocalizedClientLink>
      )}
    </section>
  )
}
